import { Link } from "@tanstack/react-router";
import { ArrowRight, MessageCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useState } from "react";
import { Button } from "@/components/ui/button";

type QA = { q: string; a: string };
type Bucket = { id: string; label: string; items: QA[] };

export const WEB_PRICING_FAQS: Bucket[] = [
  {
    id: "ownership",
    label: "Ownership & terms",
    items: [
      { q: "How long does it take to build a website?", a: "Typical websites are built in less than 7 business days once content and approvals are ready. Larger websites, ecommerce, and custom integrations may take longer; we confirm the schedule before starting." },
      {
        q: "Do I really own the website and the code?",
        a: "Yes — from day one. No cookie-cutter templates, no renting your site from us, no waiting period. You get the full GitHub repository, you own the code outright, and you can move the site to any host or developer whenever you want.",
      },
      {
        q: "Is there a contract or monthly fee for the website?",
        a: "No. Every website package is a one-time payment — no contract, no monthly fees. You own the site outright the moment it launches.",
      },
      {
        q: "Is hosting really included for the full year?",
        a: "Yes — every package includes 12 months of hosting on Cloudflare's global edge network with free SSL, daily backups, DDoS protection and uptime monitoring. After year one, hosting renews at our standard rate (and you can always move it to your own Cloudflare account at zero cost).",
      },
      {
        q: "What if I need more pages later?",
        a: "Extra pages are $85 each on any package, added whenever you need them. Many clients start with the Starter or Pro package and grow the site as the business grows.",
      },
      {
        q: "What's reporting like?",
        a: "Every website we build comes with its own built-in reporting dashboard. Log in anytime to see your traffic and how each page is doing — no waiting on a report, no PDFs, no jargon.",
      },
    ],
  },
  {
    id: "billing",
    label: "How payment works",
    items: [
      {
        q: "Website packages ($1,850–$5,950) — what are the payment terms?",
        a: "One-time payment, typically split 50/50 — half to start, half at launch. No monthly fees, no contract — you own the site outright the moment it launches.",
      },
      {
        q: "Are payment plans available?",
        a: "Yes. The payment can be split into 2 or 3 payments tied to milestones (kickoff, design approval, launch) at no extra cost. Just ask on the proposal call and we'll structure it to fit your cash flow.",
      },
      {
        q: "Can I add monthly marketing later?",
        a: "Absolutely. Most website clients add a monthly marketing package after launch when they're ready for more calls. Marketing packages are month-to-month — no long-term contract.",
      },
    ],
  },

];

export const LEAD_GEN_PRICING_FAQS: Bucket[] = [
  {
    id: "billing",
    label: "Billing & cancellation",
    items: [
      { q: "What is included in each marketing package?", a: "Starter ($950/month) includes Local SEO and Google Maps rankings. Growth ($1,650/month) includes Local SEO, Google Maps, AI & GEO optimization, and social media marketing. Expert ($2,750/month) includes multi-city Local SEO, Google Maps, AI & GEO, social media, PPC ad management, and reputation management. Ad spend not included." },
      { q: "Which package includes reputation management?", a: "Reputation management and PPC ad management are included only in Expert Marketing. Growth adds AI search optimization and social media to the Starter services." },
      {
        q: "Are lead generation packages really month-to-month?",
        a: "Yes. No 6- or 12-month lock-in. Billing renews each month on the same date and you can cancel any time with 30 days' notice — enough runway for us to wrap up work and hand off accounts cleanly.",
      },
      {
        q: "What happens if I cancel?",
        a: "We pause new work at the end of your paid period, hand over your Google Business Profile, ad accounts, analytics and any content we created on your behalf. You keep everything that's in your name.",
      },
      {
        q: "Does ad spend come out of the monthly fee?",
        a: "No. Ad spend is not included. Google, Meta, LSA and any other platform bills it directly to your card — you keep full control and visibility. The monthly fee covers our work: strategy, setup, and optimization.",
      },
      {
        q: "Do I need a website built by you to use these packages?",
        a: "No. We can run lead generation on whatever website you already have, as long as it's functional, mobile-friendly, and can capture leads. If it can't, we'll tell you up front and recommend fixes before we start spending your time and money.",
      },
    ],
  },
  {
    id: "leads",
    label: "What counts as results",
    items: [
      {
        q: "How do you define a 'lead'?",
        a: "A real human who contacted your business through a tracked channel — phone call, form fill, chat, booking, or text — that you can follow up with. We don't count impressions, clicks, or bot traffic as leads.",
      },
      {
        q: "How fast will I see results?",
        a: "Local SEO and Google Maps work typically shows movement in 60–90 days and meaningful ranking gains in 4–6 months. PPC and Local Services Ads can produce calls in the first week. We set realistic expectations up front.",
      },
      {
        q: "Do you guarantee #1 rankings?",
        a: "No — and anyone who does is lying. Google doesn't sell guaranteed positions and the algorithm changes constantly. What we do guarantee is that you'll know exactly what's being worked on, what's improving, and where you stand against competitors every single month.",
      },
      {
        q: "How do I see my results?",
        a: "When we build your website, we integrate a built-in dashboard — log in anytime and see your traffic and how each page is doing yourself. No waiting on a report, no 80-page PDF, no jargon.",
      },
    ],
  },
];

function FaqList({ buckets }: { buckets: Bucket[] }) {
  return (
    <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_2fr] lg:gap-14">
      <aside className="hidden lg:block">
        <div className="sticky top-24 space-y-2">
          <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Topics
          </div>
          {buckets.map((b) => (
            <a
              key={b.id}
              href={`#faq-${b.id}`}
              className="block rounded-lg border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:border-brand-gold-deep/50 hover:text-brand-gold-deep"
            >
              {b.label}
            </a>
          ))}
        </div>
      </aside>

      <div className="space-y-10">
        {buckets.map((bucket) => (
          <div key={bucket.id} id={`faq-${bucket.id}`} className="scroll-mt-24">
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-brand-gold-deep">
              {bucket.label}
            </div>
            <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card px-5 shadow-card">
              {bucket.items.map((item) => (
                <AccordionItem key={item.q} value={item.q} className="border-border last:border-b-0">
                  <AccordionTrigger className="py-4 text-left text-base font-semibold text-foreground hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </div>
  );
}

// Combined export for SEO JSON-LD (covers both tracks)
export const PRICING_FAQS: Bucket[] = [...WEB_PRICING_FAQS, ...LEAD_GEN_PRICING_FAQS];

export function PricingFaq() {
  const [track, setTrack] = useState<"web" | "leads">("web");
  const buckets = track === "web" ? WEB_PRICING_FAQS : LEAD_GEN_PRICING_FAQS;
  const intro = "Answers about package inclusions, payment, hosting, and monthly marketing.";

  return (
    <section id="pricing-faq" className="scroll-mt-24 bg-section-alt py-16 lg:py-20">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold-deep/30 bg-brand-gold/5 px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
            <MessageCircle className="h-3.5 w-3.5" />
            FAQs &amp; Terms
          </div>
          <h2 className="mt-4 text-3xl font-extrabold text-foreground sm:text-4xl">
            Pricing <span className="text-gradient-gold-ink">FAQs &amp; terms</span>
          </h2>
          <p className="mt-3 text-sm text-muted-foreground sm:text-base">{intro}</p>
          <p className="mt-2 text-xs italic text-muted-foreground">
            Plain-English summary — full terms live in your signed agreement.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3" role="tablist" aria-label="Pricing questions">
          <Button role="tab" aria-selected={track === "web"} variant={track === "web" ? "secondary" : "outline"} onClick={() => setTrack("web")}>Website questions</Button>
          <Button role="tab" aria-selected={track === "leads"} variant={track === "leads" ? "secondary" : "outline"} onClick={() => setTrack("leads")}>Marketing questions</Button>
        </div>
        <FaqList buckets={buckets} />

        <div className="mt-14 flex flex-col items-center justify-center gap-4 rounded-2xl border border-brand-gold-deep/30 bg-gradient-to-r from-brand-gold/10 via-card to-card p-6 text-center sm:flex-row sm:gap-6 sm:text-left">
          <div>
            <div className="text-base font-bold text-foreground">Still have questions?</div>
            <p className="text-sm text-muted-foreground">
              Hop on a no-pressure call. We'll walk you through pricing, scope and timeline.
            </p>
          </div>
          <Link
            to="/contact"
            className="group inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-gradient-gold px-5 py-3 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Request a Quote
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
